import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useMemo,
  useEffect,
  useState,
  ChangeEvent,
} from 'react';
import { useFormikContext } from 'formik';
import {
  Typography,
  Stack,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  AutocompleteRenderGetTagProps,
  Box,
  InputAdornment,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import {
  CustomAutocomplete,
  AccessFeature,
  OrderMFlexQR,
} from '~components/index';
import { hasError } from '~utils/formHelpers';
import { getValidServiceType } from '~utils/order';
import { parseSkillsStringToArray } from '~utils/vehicle';
import { PlanFeaturesTypes } from '~globals/types/enums';
import { getSuffixCapacity } from '~utils/capacityTypes';
import { useToggle, useCurrentCapacityTypeInfo } from '~hooks/index';

import {
  FIELDS_NAME,
  getInitialValues,
  SECTIONS_LABELS,
  getFieldLabel,
  getErrorsBySection,
} from '../../utils';
import { useOrderFormContext } from '../../OrderFormContext';
import { OrderFormSectionContainer } from '../../styles';
import {
  OrderEmailsChip,
  OrderEmailsChipWrapper,
  OrderFormActionChip,
} from './styles';

const OrderFormVisitDetailsSection = (): ReactElement => {
  const currentCapacities = useCurrentCapacityTypeInfo();

  const [emailValue, setEmailValue] = useState<string>('');
  const {
    values,
    errors,
    touched,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    submitCount,
  } = useFormikContext<ReturnType<typeof getInitialValues>>();

  const [isExpanded, toggleIsExpanded] = useToggle(false);

  const onChangeIsExpanded = useCallback(
    (_event: SyntheticEvent, expanded: boolean) => {
      toggleIsExpanded(expanded);
    },
    [toggleIsExpanded],
  );

  const currentErrorsList = useMemo(
    () => getErrorsBySection(errors, SECTIONS_LABELS.VisitDetails),
    [errors],
  );

  useEffect(() => {
    if (submitCount > 0 && currentErrorsList.length > 0) {
      toggleIsExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentErrorsList, submitCount]);

  const {
    itemData,
    refs: { skillsNeededAutocompleteRef, serviceTypeListAutocompleteRef },
    enumSelects: {
      operationTypes,
      priorityTypes,
      skillsNeededTypes,
      servicesTypes,
    },
  } = useOrderFormContext();

  const isDisabledCashAmount = useMemo(
    () => !values[FIELDS_NAME.DELIVERY_REQUEST_PAYMENT_ON_DESTINY],
    [values],
  );

  const setDefaultSkillsNeeded = useCallback(() => {
    const defaultSkillNeeded = parseSkillsStringToArray(itemData?.skillsNeeded);

    if (defaultSkillNeeded.length) {
      skillsNeededAutocompleteRef.current?.setValue(defaultSkillNeeded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemData?.skillsNeeded]);

  useEffect(() => {
    setDefaultSkillsNeeded();
  }, [setDefaultSkillsNeeded]);

  const handleChangeSkills = useCallback(
    (_e: SyntheticEvent<Element, Event>, newVals: string[]) => {
      setFieldValue(FIELDS_NAME.DELIVERY_SKILLS_NEEDED, newVals);
    },
    [setFieldValue],
  );

  const handleBlurSkills = useCallback(() => {
    setFieldTouched(FIELDS_NAME.DELIVERY_SKILLS_NEEDED, true);
  }, [setFieldTouched]);

  const setDefaultServiceType = useCallback(() => {
    const defaultServiceType = getValidServiceType(
      itemData?.serviceType ?? null,
      servicesTypes,
    );

    if (defaultServiceType) {
      serviceTypeListAutocompleteRef.current?.setValue(defaultServiceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemData?.serviceType]);

  useEffect(() => {
    setDefaultServiceType();
  }, [setDefaultServiceType]);

  const handleChangeServiceType = useCallback(
    (_e: SyntheticEvent<Element, Event>, newVal: string | null) => {
      setFieldValue(FIELDS_NAME.SERVICE_TYPE, newVal);
    },
    [setFieldValue],
  );

  const handleBlurServiceType = useCallback(() => {
    setFieldTouched(FIELDS_NAME.SERVICE_TYPE, true);
  }, [setFieldTouched]);

  const handleNewTag = useCallback(
    (tag: string) => {
      const newTag = tag.trim();
      if (newTag !== '') {
        const tagsArr = values[FIELDS_NAME.DELIVERY_SELLER_MAIL]
          ? values[FIELDS_NAME.DELIVERY_SELLER_MAIL].split(',').filter(Boolean)
          : [];
        tagsArr.push(tag);
        setEmailValue('');
        setFieldValue(FIELDS_NAME.DELIVERY_SELLER_MAIL, tagsArr.join(','));
        setFieldTouched(FIELDS_NAME.DELIVERY_SELLER_MAIL, true);
      }
    },
    [values, setFieldValue, setFieldTouched],
  );

  const handleAddEmailTag = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleNewTag(emailValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailValue],
  );

  const handleDeleteEmailTag = useCallback(
    (tagIndex: number, tags: string[]) => {
      const updatedTags = tags.filter((_, index) => index !== tagIndex);
      setFieldValue(FIELDS_NAME.DELIVERY_SELLER_MAIL, updatedTags.join(','));
    },
    [setFieldValue],
  );

  const handleChangeEmailText = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setEmailValue(e.target.value),
    [setEmailValue],
  );

  const handleEmailTextBlur = useCallback(() => {
    handleNewTag(emailValue);
    setFieldTouched(FIELDS_NAME.DELIVERY_SELLER_MAIL, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailValue, setFieldTouched]);

  const renderTagsSkills = useCallback(
    (vals: string[], getTagsProps: AutocompleteRenderGetTagProps) =>
      vals.map((val, index) => (
        // eslint-disable-next-line react/jsx-key
        <OrderFormActionChip
          label={val}
          size="small"
          {...getTagsProps({ index })}
        />
      )),
    [],
  );

  const renderEmailTags = useCallback((vals: string[]) => {
    const filteredVals = vals.filter((val) => val.length);
    if (!filteredVals.length) return null;
    return (
      <OrderEmailsChipWrapper direction={'row'}>
        {filteredVals.map((val, index) => (
          <OrderEmailsChip
            key={`wrapper-tag-${index}`}
            label={val}
            size="small"
            onDelete={() => handleDeleteEmailTag(index, filteredVals)}
          />
        ))}
      </OrderEmailsChipWrapper>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OrderFormSectionContainer
      expanded={isExpanded}
      onChange={onChangeIsExpanded}
      disableGutters
      elevation={0}
      variant="outlined"
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel-order-form-visit-details-content"
        id="panel-order-form-visit-details-header"
      >
        <Typography component="h6" fontWeight="bold">
          {SECTIONS_LABELS.VisitDetails}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_CODE)}
              fullWidth
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_CODE)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_CODE)}
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_OPERATION)}
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_OPERATION)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_OPERATION)}
              select
              fullWidth
            >
              {operationTypes.map((operation) => (
                <MenuItem key={operation.value} value={String(operation.value)}>
                  {operation.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_QUANTITY)}
              fullWidth
              autoComplete="off"
              type="number"
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_QUANTITY)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_QUANTITY)}
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_NUMBER_OFF_PACKAGES)}
              fullWidth
              type="number"
              error={hasError(
                touched,
                errors,
                FIELDS_NAME.DELIVERY_NUMBER_OFF_PACKAGES,
              )}
              {...getFieldProps(FIELDS_NAME.DELIVERY_NUMBER_OFF_PACKAGES)}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label={getFieldLabel(
                FIELDS_NAME.DELIVERY_UNIT_1,
                currentCapacities,
              )}
              fullWidth
              type="number"
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_UNIT_1)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_UNIT_1)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getSuffixCapacity(currentCapacities.unit1CapacityTypeId)}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label={getFieldLabel(
                FIELDS_NAME.DELIVERY_UNIT_2,
                currentCapacities,
              )}
              fullWidth
              type="number"
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_UNIT_2)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_UNIT_2)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {getSuffixCapacity(currentCapacities.unit2CapacityTypeId)}
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.PRICE)}
              autoComplete="off"
              fullWidth
              type="number"
              error={hasError(touched, errors, FIELDS_NAME.PRICE)}
              {...getFieldProps(FIELDS_NAME.PRICE)}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              sx={{ marginLeft: 0 }}
              control={
                <Switch
                  {...getFieldProps(
                    FIELDS_NAME.DELIVERY_REQUEST_PAYMENT_ON_DESTINY,
                  )}
                  checked={
                    values[FIELDS_NAME.DELIVERY_REQUEST_PAYMENT_ON_DESTINY]
                  }
                />
              }
              label={getFieldLabel(
                FIELDS_NAME.DELIVERY_REQUEST_PAYMENT_ON_DESTINY,
              )}
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_CASH_AMOUNT)}
              autoComplete="off"
              type="number"
              disabled={isDisabledCashAmount}
              error={hasError(
                touched,
                errors,
                FIELDS_NAME.DELIVERY_CASH_AMOUNT,
              )}
              {...getFieldProps(FIELDS_NAME.DELIVERY_CASH_AMOUNT)}
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_PRIORITY)}
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_PRIORITY)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_PRIORITY)}
              select
              sx={{ flex: 1 }}
            >
              {priorityTypes.map((priority) => (
                <MenuItem key={priority.value} value={String(priority.value)}>
                  {priority.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <AccessFeature.TextField
            validFeature={PlanFeaturesTypes.LimitPaid}
            fullWidth
            value={emailValue}
            label={getFieldLabel(FIELDS_NAME.DELIVERY_SELLER_MAIL)}
            error={hasError(touched, errors, FIELDS_NAME.DELIVERY_SELLER_MAIL)}
            onChange={handleChangeEmailText}
            onKeyDown={handleAddEmailTag}
            onBlur={handleEmailTextBlur}
            InputProps={{
              startAdornment: renderEmailTags(
                values[FIELDS_NAME.DELIVERY_SELLER_MAIL].split(','),
              ),
              style: {
                alignItems: 'flex-start',
              },
            }}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_INVOICE)}
              fullWidth
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_INVOICE)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_INVOICE)}
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_SHIPMENT)}
              fullWidth
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_SHIPMENT)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_SHIPMENT)}
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.DELIVERY_TAGS)}
              fullWidth
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.DELIVERY_TAGS)}
              {...getFieldProps(FIELDS_NAME.DELIVERY_TAGS)}
            />
          </Stack>

          <CustomAutocomplete
            innerRef={skillsNeededAutocompleteRef}
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            renderTags={renderTagsSkills}
            disableClearable
            options={skillsNeededTypes}
            label={getFieldLabel(FIELDS_NAME.DELIVERY_SKILLS_NEEDED)}
            name={FIELDS_NAME.DELIVERY_SKILLS_NEEDED}
            onChange={handleChangeSkills}
            onBlur={handleBlurSkills}
            fullWidth
            getOptionLabel={(option) => option}
          />

          <AccessFeature.AutoComplete
            validFeature={PlanFeaturesTypes.LimitNotPro}
            innerRef={serviceTypeListAutocompleteRef}
            options={servicesTypes}
            label={getFieldLabel(FIELDS_NAME.SERVICE_TYPE)}
            name={FIELDS_NAME.SERVICE_TYPE}
            onChange={handleChangeServiceType}
            onBlur={handleBlurServiceType}
            error={hasError(touched, errors, FIELDS_NAME.SERVICE_TYPE)}
            fullWidth
            getOptionLabel={(option) => option}
          />

          <AccessFeature.TextField
            validFeature={PlanFeaturesTypes.LimitPaid}
            label={getFieldLabel(FIELDS_NAME.QR_CODE)}
            error={hasError(touched, errors, FIELDS_NAME.QR_CODE)}
            {...getFieldProps(FIELDS_NAME.QR_CODE)}
            InputProps={{
              endAdornment: values[FIELDS_NAME.QR_CODE] && (
                <Box sx={{ top: -10, position: 'relative', marginLeft: 2 }}>
                  <OrderMFlexQR.IconWithTooltipInfo
                    qrData={values[FIELDS_NAME.QR_CODE]}
                  />
                </Box>
              ),
            }}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </AccordionDetails>
    </OrderFormSectionContainer>
  );
};

export default OrderFormVisitDetailsSection;
